import { ReactNode, createContext, isValidElement, useContext, useMemo } from 'react';

import { Button, ButtonProps, CircularProgress } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { SystemStyleObject, Theme } from '@mui/system';

export const AppButtonCollapsedContext = createContext(false);

export type AppButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'link'
  | 'p-rounded'
  | 's-rounded'
  | 'p-alert'
  | 's-alert'
  | 'p-success'
  | 's-success';

export type AppButtonSize = 'small' | 'medium' | 'large';

export interface AppButtonProps extends Omit<ButtonProps, 'variant' | 'size' | 'color'> {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  collapsed?: boolean;
  loading?: boolean;
}

const BASE: SystemStyleObject<Theme> = {
  fontFamily: 'Neue Frutiger World',
  textTransform: 'none',
  letterSpacing: 0,
  gap: '4px',
  boxShadow: 'none !important',
  minWidth: 0,
  whiteSpace: 'nowrap',
  '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
};

const SIZE: Record<AppButtonSize, SystemStyleObject<Theme>> = {
  small: {
    px: '12px',
    py: '4px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  medium: {
    px: '16px',
    py: '6px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.6,
  },
  large: {
    px: '24px',
    py: '8px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.5,
  },
};

const ICON_SIZE: Record<AppButtonVariant, Record<AppButtonSize, string>> = {
  primary: { small: '14px', medium: '16px', large: '18px' },
  secondary: { small: '14px', medium: '16px', large: '18px' },
  ghost: { small: '14px', medium: '14px', large: '16px' },
  link: { small: '14px', medium: '14px', large: '16px' },
  'p-rounded': { small: '14px', medium: '16px', large: '18px' },
  's-rounded': { small: '14px', medium: '16px', large: '18px' },
  'p-alert': { small: '14px', medium: '16px', large: '18px' },
  's-alert': { small: '14px', medium: '16px', large: '18px' },
  'p-success': { small: '14px', medium: '16px', large: '18px' },
  's-success': { small: '14px', medium: '16px', large: '18px' },
};

const COLLAPSED_SIZE: Record<AppButtonSize, SystemStyleObject<Theme>> = {
  small: { px: '8px', py: '4px', minWidth: 0, '& .button-text': { display: 'none' } },
  medium: { px: '12px', py: '6px', minWidth: 0, '& .button-text': { display: 'none' } },
  large: { px: '16px', py: '8px', minWidth: 0, '& .button-text': { display: 'none' } },
};

export default function AppButton({
  variant = 'primary',
  size = 'medium',
  collapsed = false,
  loading = false,
  children,
  sx,
  startIcon,
  disabled,
  ...props
}: AppButtonProps) {
  const contextCollapsed = useContext(AppButtonCollapsedContext);
  const isCollapsed = collapsed || contextCollapsed;
  const theme = useTheme();
  const { accent, grey, error, success } = theme.palette;

  const VARIANT: Record<AppButtonVariant, SystemStyleObject<Theme>> = useMemo(
    () => ({
      primary: {
        backgroundColor: accent.main,
        color: accent.contrastText,
        border: '1px solid transparent',
        '&:hover': { backgroundColor: accent.dark },
        '&.Mui-disabled': {
          backgroundColor: alpha(accent.main, 0.35),
          color: alpha(accent.contrastText, 0.5),
        },
      },
      secondary: {
        backgroundColor: 'transparent',
        color: accent.main,
        border: `1px solid ${accent.main}`,
        '&:hover': {
          backgroundColor: accent.main,
          color: accent.contrastText,
          border: `1px solid ${accent.main}`,
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          color: grey[400],
          border: `1px solid ${grey[300]}`,
        },
      },
      ghost: {
        backgroundColor: 'transparent',
        color: grey[700],
        border: '1px solid transparent',
        '&:hover': { backgroundColor: accent.light, color: accent.dark },
        '&.Mui-disabled': { backgroundColor: 'transparent', color: grey[400] },
      },
      link: {
        backgroundColor: 'transparent',
        color: accent.main,
        border: 'none',
        padding: '0 !important',
        '&:hover': { backgroundColor: 'transparent', color: accent.dark },
        '&.Mui-disabled': { backgroundColor: 'transparent', color: grey[400] },
      },
      'p-rounded': {
        backgroundColor: accent.main,
        color: accent.contrastText,
        border: '1px solid transparent',
        borderRadius: '100px !important',
        '&:hover': { backgroundColor: accent.dark },
        '&.Mui-disabled': {
          backgroundColor: alpha(accent.main, 0.35),
          color: alpha(accent.contrastText, 0.5),
        },
      },
      's-rounded': {
        backgroundColor: 'transparent',
        color: accent.main,
        border: `1px solid ${accent.main}`,
        borderRadius: '100px !important',
        '&:hover': {
          backgroundColor: accent.main,
          color: accent.contrastText,
          border: `1px solid ${accent.main}`,
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          color: grey[500],
          border: `1px solid ${grey[500]}`,
        },
      },
      'p-alert': {
        backgroundColor: error.main,
        color: error.contrastText,
        border: '1px solid transparent',
        '&:hover': { backgroundColor: error.dark },
        '&.Mui-disabled': {
          backgroundColor: alpha(error.main, 0.35),
          color: alpha(error.contrastText, 0.5),
        },
      },
      's-alert': {
        backgroundColor: 'transparent',
        color: error.main,
        border: `1px solid ${error.main}`,
        '&:hover': {
          backgroundColor: error.main,
          color: error.contrastText,
          border: `1px solid ${error.main}`,
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          color: grey[400],
          border: `1px solid ${grey[300]}`,
        },
      },
      'p-success': {
        backgroundColor: success.main,
        color: success.contrastText,
        border: '1px solid transparent',
        '&:hover': { backgroundColor: success.dark },
        '&.Mui-disabled': {
          backgroundColor: alpha(success.main, 0.35),
          color: alpha(success.contrastText, 0.5),
        },
      },
      's-success': {
        backgroundColor: 'transparent',
        color: success.main,
        border: `1px solid ${success.main}`,
        '&:hover': {
          backgroundColor: success.main,
          color: success.contrastText,
          border: `1px solid ${success.main}`,
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
          color: grey[400],
          border: `1px solid ${grey[300]}`,
        },
      },
    }),
    [accent, grey, error, success],
  );

  const resolvedSx = sx != null ? (Array.isArray(sx) ? sx : [sx]) : [];

  const iconSx: SystemStyleObject<Theme> = {
    '& .MuiButton-startIcon svg, & .MuiButton-endIcon svg': {
      fontSize: ICON_SIZE[variant][size],
    },
  };

  return (
    <Button
      variant="text"
      disabled={loading || disabled}
      startIcon={
        loading ? <CircularProgress size={ICON_SIZE[variant][size]} color="inherit" /> : startIcon
      }
      sx={[
        BASE,
        SIZE[size],
        VARIANT[variant],
        iconSx,
        ...(isCollapsed ? [COLLAPSED_SIZE[size]] : []),
        ...resolvedSx,
      ]}
      {...props}
    >
      {wrapButtonChildren(children)}
    </Button>
  );
}

// Wrap primitive children (strings/numbers) in .button-text so the collapsed
// CSS rule can hide them. React elements (e.g. <input> in UploadImportFile,
// custom spans in PageTitleActionButton) are passed through unchanged.
function wrapButtonChildren(children: ReactNode): ReactNode {
  if (children == null) return null;
  // Single React element — return as-is, no key mutation
  if (isValidElement(children)) return children;
  // Array — process each child individually
  if (Array.isArray(children)) {
    return children.map((child, i) => {
      if (child == null || isValidElement(child)) return child;
      return (
        <span key={i} className="button-text">
          {child}
        </span>
      );
    });
  }
  // Primitive (string / number)
  return <span className="button-text">{children}</span>;
}
